import { useState, useMemo, useEffect, useRef } from "react";

const PAD = 12;
const NODE_W = 14;
const GAP = 10;
const MIN_W = 320;
const MIN_NODE_H = 4;
const MIN_SCALE = 0.2;

const STATUS_ORDER = ["Open", "In Progress", "Complete", "Close", "Reopen", "Reject"];

const STATUS_COLOR = {
  Open: "#3b82f6",
  "In Progress": "#f59e0b",
  Complete: "#22c55e",
  Close: "#94a3b8",
  Reopen: "#8b5cf6",
  Reject: "#ef4444",
};

const STATUS_MAP = {
  1: "Open", 2: "In Progress", 3: "Complete", 4: "Close",
  5: "Need More Info", 6: "Reopen", 7: "Reject",
  "1": "Open", "2": "In Progress", "3": "Complete", "4": "Close",
  "5": "Need More Info", "6": "Reopen", "7": "Reject",
  Open: "Open", Active: "In Progress", "In Progress": "In Progress",
  Resolved: "Complete", Complete: "Complete", Closed: "Close", Close: "Close",
  Reopen: "Reopen", Reopened: "Reopen", Reject: "Reject", Draft: "Draft",
};

const LIGHT = {
  nodeLabel: "#374151", linkDefault: "#d1d5db", linkHighlight: "#6D17CE",
  tooltipBg: "#FFFFFF", tooltipBorder: "#e5e7eb",
  tooltipName: "#141414", tooltipLabel: "#6b7280", tooltipValue: "#141414",
};

const DARK = {
  nodeLabel: "#e2e8f0", linkDefault: "#475569", linkHighlight: "#3b82f6",
  tooltipBg: "#1e293b", tooltipBorder: "#475569",
  tooltipName: "#f1f5f9", tooltipLabel: "#94a3b8", tooltipValue: "#f1f5f9",
};

const TEAM_COLORS = [
  "#6D17CE", "#0f3cc9", "#0891b2", "#059669", "#d97706",
  "#dc2626", "#7c3aed", "#db2777", "#65a30d", "#0284c7",
];

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function shortName(email) {
  if (!email) return "Unknown";
  return email.split("@")[0].split(".").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function linkPath(link) {
  const x0 = link.source.x1;
  const x1 = link.target.x0;
  const y0 = (link.sy0 + link.sy1) / 2;
  const y1 = (link.ty0 + link.ty1) / 2;
  const xi = (x0 + x1) / 2;
  return `M ${x0} ${y0} C ${xi} ${y0}, ${xi} ${y1}, ${x1} ${y1}`;
}

function resolveStatus(t) {
  const raw = t.status ?? t.currentStage ?? "";
  return STATUS_MAP[raw] || STATUS_MAP[String(raw)] || String(raw);
}

export function buildThreeColumnLayout(projectName, middleItems, statusCounts, itemStatusMap, width, height) {
  const totalTickets = middleItems.reduce((s, e) => s + e.count, 0);
  if (totalTickets === 0) return null;

  const statuses = STATUS_ORDER.filter((s) => (statusCounts[s] || 0) > 0).map((s) => ({ name: s, value: statusCounts[s] }));
  if (!statuses.length) return null;

  const availH = height - PAD * 2;
  const midGaps = GAP * Math.max(0, middleItems.length - 1);
  const stGaps = GAP * Math.max(0, statuses.length - 1);
  const scale = Math.max(MIN_SCALE, Math.min(availH, availH - midGaps, availH - stGaps) / totalTickets);

  const xL0 = PAD, xL1 = xL0 + NODE_W;
  const xM0 = Math.floor(width / 2 - NODE_W / 2), xM1 = xM0 + NODE_W;
  const xR1 = width - PAD, xR0 = xR1 - NODE_W;

  const nodes = new Map();
  const projH = Math.max(MIN_NODE_H, totalTickets * scale);
  nodes.set(projectName, { name: projectName, group: "left", x0: xL0, x1: xL1, y0: PAD, y1: PAD + projH, value: totalTickets });

  let y = PAD;
  for (const item of middleItems) {
    const h = Math.max(MIN_NODE_H, item.count * scale);
    nodes.set(item.name, { name: item.name, group: "middle", x0: xM0, x1: xM1, y0: y, y1: y + h, value: item.count });
    y += h + GAP;
  }

  y = PAD;
  for (const st of statuses) {
    const h = Math.max(MIN_NODE_H, st.value * scale);
    nodes.set(st.name, { name: st.name, group: "right", x0: xR0, x1: xR1, y0: y, y1: y + h, value: st.value });
    y += h + GAP;
  }

  const outOff = new Map(), inOff = new Map();
  const getO = (k) => outOff.get(k) || 0, addO = (k, v) => outOff.set(k, getO(k) + v);
  const getI = (k) => inOff.get(k) || 0, addI = (k, v) => inOff.set(k, getI(k) + v);
  const links = [];

  for (const item of middleItems) {
    const src = nodes.get(projectName), tgt = nodes.get(item.name), v = item.count;
    const sy0 = src.y0 + getO(projectName) * scale, ty0 = tgt.y0 + getI(item.name) * scale;
    addO(projectName, v); addI(item.name, v);
    links.push({ source: src, target: tgt, value: v, sy0, sy1: sy0 + v * scale, ty0, ty1: ty0 + v * scale });
  }

  for (const item of middleItems) {
    for (const st of statuses) {
      const v = itemStatusMap.get(`${item.name}|${st.name}`) || 0;
      if (!v) continue;
      const src = nodes.get(item.name), tgt = nodes.get(st.name);
      const sy0 = src.y0 + getO(item.name) * scale, ty0 = tgt.y0 + getI(st.name) * scale;
      addO(item.name, v); addI(st.name, v);
      links.push({ source: src, target: tgt, value: v, sy0, sy1: sy0 + v * scale, ty0, ty1: ty0 + v * scale });
    }
  }

  return { nodes: [...nodes.values()], links, scale };
}

export const ResourceSankey = ({
  applicationName = "Project",
  tickets = [],
  userAccessMap = {},
  userTeamMap = {},
  height = 360,
  onEngineerClick,
  onStatusClick,
  embedded = false,
  darkMode = false,
}) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(MIN_W);
  const [hoveredMiddle, setHoveredMiddle] = useState(null);
  const [view, setView] = useState("team");
  const [selectedTeam, setSelectedTeam] = useState(null);

  const CLR = darkMode ? DARK : LIGHT;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect()?.width;
    if (typeof w === "number" && w > 0) setWidth(Math.floor(w));
    const ro = new ResizeObserver((entries) => {
      const cw = entries?.[0]?.contentRect?.width;
      if (typeof cw === "number" && cw > 0) setWidth(Math.floor(cw));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const teamData = useMemo(() => {
    const teamMap = new Map();
    const sc = {};
    const tsm = new Map();

    for (const t of tickets) {
      const assignee = t.assign || t.assignee;
      if (!assignee) continue;
      const info = userTeamMap[assignee];
      const teamName = info?.teamName || "Unknown Team";
      const statusStr = resolveStatus(t);

      teamMap.set(teamName, (teamMap.get(teamName) || 0) + 1);
      sc[statusStr] = (sc[statusStr] || 0) + 1;
      tsm.set(`${teamName}|${statusStr}`, (tsm.get(`${teamName}|${statusStr}`) || 0) + 1);
    }

    return {
      items: [...teamMap.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
      statusCounts: sc,
      itemStatusMap: tsm,
    };
  }, [tickets, userTeamMap]);

  const engData = useMemo(() => {
    const engMap = new Map();
    const sc = {};
    const esm = new Map();

    for (const t of tickets) {
      const assignee = t.assign || t.assignee;
      if (!assignee || !(assignee in userAccessMap)) continue;
      if (selectedTeam) {
        const info = userTeamMap[assignee];
        if (!info || info.teamName !== selectedTeam) continue;
      }

      const name = shortName(assignee);
      const statusStr = resolveStatus(t);

      engMap.set(name, (engMap.get(name) || 0) + 1);
      sc[statusStr] = (sc[statusStr] || 0) + 1;
      esm.set(`${name}|${statusStr}`, (esm.get(`${name}|${statusStr}`) || 0) + 1);
    }

    return {
      items: [...engMap.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
      statusCounts: sc,
      itemStatusMap: esm,
    };
  }, [tickets, userAccessMap, userTeamMap, selectedTeam]);

  const activeData = view === "team" ? teamData : engData;

  const computedH = useMemo(() => {
    const stCount = STATUS_ORDER.filter((s) => (activeData.statusCounts[s] || 0) > 0).length;
    return Math.max(height, activeData.items.length * 32 + 48, stCount * 32 + 48);
  }, [activeData, height]);

  const layoutW = Math.max(MIN_W, width);

  const layout = useMemo(
    () => buildThreeColumnLayout(applicationName, activeData.items, activeData.statusCounts, activeData.itemStatusMap, layoutW, computedH),
    [applicationName, activeData, layoutW, computedH]
  );

  const hoverActive = hoveredMiddle !== null;
  const isLinkHL = (l) => hoverActive && (l.source.name === hoveredMiddle || l.target.name === hoveredMiddle);
  const isNodeHL = (n) => {
    if (!hoverActive) return true;
    if (n.name === hoveredMiddle) return true;
    return layout?.links.some((l) => isLinkHL(l) && (l.source.name === n.name || l.target.name === n.name));
  };

  const tooltipSvg = useMemo(() => {
    if (!hoveredMiddle || !layout) return null;
    const node = layout.nodes.find((n) => n.name === hoveredMiddle);
    if (!node) return null;

    const outgoing = layout.links.filter((l) => l.source.name === hoveredMiddle && l.target.group === "right");
    const rows = [];

    if (view === "engineer") {
      const entry = Object.entries(userAccessMap).find(([email]) => shortName(email) === hoveredMiddle);
      const role = entry ? (entry[1] === 3 ? "Manager" : entry[1] === 2 ? "Lead" : "Engineer") : "Engineer";
      rows.push({ label: `Role: ${role}`, value: "", meta: true });
    }

    for (const l of outgoing) rows.push({ label: l.target.name, value: l.value });
    rows.push({ label: "Total", value: node.value, bold: true });
    if (view === "team") rows.push({ label: "Click to drill down", value: "", meta: true });

    const lineH = 18, boxW = 210, boxH = 42 + rows.length * lineH;
    const side = node.x0 < layoutW / 2 ? "right" : "left";
    const anchorX = side === "right" ? node.x1 + 16 : node.x0 - 16;
    let x = side === "right" ? anchorX : anchorX - boxW;
    x = clamp(x, 8, Math.max(8, layoutW - boxW - 8));
    let ty = (node.y0 + node.y1) / 2 - boxH / 2;
    ty = clamp(ty, 8, Math.max(8, computedH - boxH - 8));

    return (
      <g style={{ pointerEvents: "none" }}>
        <rect x={x} y={ty} width={boxW} height={boxH} rx={12}
          fill={CLR.tooltipBg} stroke={CLR.tooltipBorder} strokeWidth={1} opacity={0.98}
          filter="drop-shadow(0 4px 12px rgba(0,0,0,0.1))" />
        <text x={x + 14} y={ty + 22} fontSize="12" fontWeight="700" fill={CLR.tooltipName}>
          {hoveredMiddle} ({node.value})
        </text>
        {rows.map((row, i) => (
          <g key={i}>
            <text x={x + 14} y={ty + 42 + i * lineH} fontSize="11"
              fontWeight={row.bold ? "700" : "400"} fontStyle={row.meta ? "italic" : "normal"}
              fill={row.bold ? CLR.tooltipValue : CLR.tooltipLabel}>
              {row.label}
            </text>
            {row.value !== "" && (
              <text x={x + boxW - 14} y={ty + 42 + i * lineH} fontSize="11"
                fontWeight={row.bold ? "700" : "400"} fill={CLR.tooltipValue} textAnchor="end">
                {row.value}
              </text>
            )}
          </g>
        ))}
      </g>
    );
  }, [hoveredMiddle, layout, layoutW, computedH, CLR, userAccessMap, view]);

  function handleMiddleClick(nodeName) {
    if (view === "team") {
      setSelectedTeam(nodeName);
      setView("engineer");
      setHoveredMiddle(null);
    } else if (view === "engineer" && onEngineerClick) {
      onEngineerClick(nodeName);
    }
  }

  function goBackToTeams() {
    setView("team");
    setSelectedTeam(null);
    setHoveredMiddle(null);
  }

  const middleLabel = view === "team" ? "Team" : "Engineer";
  const subtitle = view === "team"
    ? "Project \u2192 Team \u2192 Status"
    : `Project \u2192 ${selectedTeam} \u2192 Engineer \u2192 Status`;

  const svgContent = layout && (
    <svg width="100%" viewBox={`0 0 ${layoutW} ${computedH}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${applicationName} Ticket Flow`}>
      {[...layout.links].sort((a, b) => (isLinkHL(a) ? 1 : 0) - (isLinkHL(b) ? 1 : 0)).map((link, i) => (
        <path key={i} d={linkPath(link)} fill="none"
          stroke={isLinkHL(link) ? CLR.linkHighlight : CLR.linkDefault}
          strokeOpacity={!hoverActive ? 0.4 : isLinkHL(link) ? 0.65 : 0.08}
          strokeWidth={Math.max(1, link.value * layout.scale)}>
          <title>{`${link.source.name} → ${link.target.name}: ${link.value}`}</title>
        </path>
      ))}

      {layout.nodes.map((node, i) => {
        const fill = node.group === "left" ? "#3e0084"
          : node.group === "right" ? (STATUS_COLOR[node.name] || "#64748b")
          : view === "team" ? (TEAM_COLORS[i % TEAM_COLORS.length]) : "#64748b";
        const clickable = node.group === "middle" || node.group === "right";

        return (
          <g key={i}>
            <rect x={node.x0} y={node.y0} width={node.x1 - node.x0} height={Math.max(2, node.y1 - node.y0)}
              rx={3} fill={fill}
              opacity={isNodeHL(node) ? (hoveredMiddle === node.name ? 0.98 : 1) : 0.25}
              style={{ transition: "opacity 120ms ease", cursor: clickable ? "pointer" : "default" }}
              onMouseEnter={() => node.group === "middle" && setHoveredMiddle(node.name)}
              onMouseLeave={() => node.group === "middle" && setHoveredMiddle(null)}
              onClick={() => {
                if (node.group === "middle") handleMiddleClick(node.name);
                if (node.group === "right" && onStatusClick) onStatusClick(node.name);
              }}>
              <title>{`${node.name}: ${node.value}`}</title>
            </rect>
            {!(node.group === "middle" && hoveredMiddle === node.name) && (
              <text x={node.group === "right" ? node.x0 - 6 : node.x1 + 6}
                y={(node.y0 + node.y1) / 2} dy="0.35em"
                textAnchor={node.group === "right" ? "end" : "start"}
                fontSize="11" fontWeight="600" fill={CLR.nodeLabel}
                opacity={isNodeHL(node) ? 1 : 0.25}
                style={{ pointerEvents: "none", transition: "opacity 120ms ease" }}>
                {`${node.name} (${node.value})`}
              </text>
            )}
          </g>
        );
      })}

      {tooltipSvg}
    </svg>
  );

  const emptyState = (
    <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No data available</div>
  );

  if (embedded) {
    return (
      <div ref={containerRef} className="w-full">
        {view === "engineer" && (
          <button onClick={goBackToTeams}
            className="mb-2 flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-jiojbmpurple transition">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
            Back to Teams
          </button>
        )}
        {layout ? svgContent : emptyState}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white rounded-xl border border-gray-300 shadow-sm p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {view === "engineer" && (
            <button onClick={goBackToTeams}
              className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-jiojbmpurple transition rounded-full px-2 py-1 hover:bg-jiojbm20">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
              Teams
            </button>
          )}
          <div>
            <h3 className="text-base font-black tracking-tight">Ticket Flow</h3>
            <p className="text-[11px] text-gray-400">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between text-[10px] text-gray-400 font-bold px-4 mb-0.5">
        <span>Project</span>
        <span>{middleLabel}</span>
        <span>Status</span>
      </div>

      {layout ? svgContent : emptyState}
    </div>
  );
};
